import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  Checkbox,
  Button,
  Typography,
  Popover,
  InputAdornment,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import { statusFilterOptions } from "../../../../utils/enums";

const StatusFilterPopover = ({ onFilterChange, activeFilters = [] }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState({});

  const open = Boolean(anchorEl);
  const isFilterActive = activeFilters.length > 0;

  // Initialize selectedStatuses from activeFilters
  useEffect(() => {
    if (activeFilters.length > 0) {
      const initialSelected = {};
      activeFilters.forEach((status) => {
        initialSelected[status] = true;
      });
      setSelectedStatuses(initialSelected);
    } else {
      setSelectedStatuses({});
    }
  }, [activeFilters]);

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleStatusToggle = (statusId) => {
    setSelectedStatuses((prev) => {
      const newSelected = { ...prev };
      if (newSelected[statusId]) {
        delete newSelected[statusId];
      } else {
        newSelected[statusId] = true;
      }
      return newSelected;
    });
  };

  const handleApplyFilter = () => {
    const selectedValues = Object.keys(selectedStatuses);
    onFilterChange(selectedValues);
    handleFilterClose();
  };

  const handleResetFilter = () => {
    setSelectedStatuses({});
    onFilterChange([]);
    handleFilterClose();
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredOptions = statusFilterOptions.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const componentStyles = {
    popover: {
      width: "180px",
      maxHeight: "300px",
      borderRadius: theme.spacing(1),
      boxShadow: theme.shadows[4],
      border: `1px solid ${theme.palette.divider}`,
      overflow: "hidden",
      backgroundColor: theme.palette.background.paper,
    },
    header: {
      padding: `${theme.spacing(1)} ${theme.spacing(1.5)} ${theme.spacing(0.75)} ${theme.spacing(1.5)}`,
      backgroundColor: theme.palette.grey[50],
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    searchInput: {
      "& .MuiOutlinedInput-root": {
        height: "32px",
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.spacing(0.75),
        fontSize: "12px",
        "& fieldset": {
          borderColor: theme.palette.grey[300],
        },
        "&:hover fieldset": {
          borderColor: theme.palette.primary.main,
        },
        "&.Mui-focused fieldset": {
          borderColor: theme.palette.primary.main,
          borderWidth: "1px",
        },
      },
    },
    searchIcon: {
      fontSize: 16,
      color: theme.palette.grey[500],
    },
    content: {
      maxHeight: "160px",
      overflowY: "auto",
      backgroundColor: theme.palette.background.paper,
      "&::-webkit-scrollbar": {
        width: "4px",
      },
      "&::-webkit-scrollbar-track": {
        background: theme.palette.grey[100],
      },
      "&::-webkit-scrollbar-thumb": {
        background: theme.palette.grey[300],
        borderRadius: "2px",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        background: theme.palette.grey[400],
      },
    },
    optionContainer: {
      display: "flex",
      alignItems: "center",
      padding: `${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
      cursor: "pointer",
      transition: "background-color 0.15s ease",
      "&:hover": {
        backgroundColor: theme.palette.grey[50],
      },
    },
    optionCheckbox: {
      marginRight: theme.spacing(1),
      color: theme.palette.grey[600],
      padding: theme.spacing(0.25),
      "&.Mui-checked": {
        color: theme.palette.primary.main,
      },
    },
    optionText: {
      fontSize: "12px",
      color: theme.palette.text.secondary,
      fontWeight: 400,
    },
    footer: {
      padding: `${theme.spacing(0.75)} ${theme.spacing(1.5)} ${theme.spacing(1)} ${theme.spacing(1.5)}`,
      backgroundColor: theme.palette.grey[50],
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    buttonContainer: {
      display: "flex",
      gap: theme.spacing(0.75),
    },
    resetButton: {
      flex: 1,
      fontSize: "11px",
      fontWeight: 500,
      textTransform: "none",
      borderRadius: theme.spacing(0.5),
      minWidth: "auto",
      px: theme.spacing(1.5),
    },
    applyButton: {
      flex: 1,
      fontSize: "11px",
      fontWeight: 600,
      textTransform: "none",
      borderRadius: theme.spacing(0.5),
      backgroundColor: theme.palette.primary.main,
      minWidth: "auto",
      px: theme.spacing(1.5),
      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
      },
    },
    noResultsContainer: {
      textAlign: "center",
      padding: theme.spacing(2),
    },
    noResultsText: {
      color: theme.palette.grey[500],
      fontSize: "12px",
    },
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleFilterClick}
        sx={{
          ml: 0.5,
          padding: "4px",
          backgroundColor: isFilterActive
            ? theme.palette.grey[200]
            : "transparent",
          borderRadius: theme.spacing(1),
          "&:hover": {
            backgroundColor: isFilterActive
              ? theme.palette.grey[300]
              : theme.palette.grey[100],
          },
        }}
      >
        <FilterListIcon fontSize="small" />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: componentStyles.popover }}
      >
        {/* HEADER */}
        <Box sx={componentStyles.header}>
          <TextField
            size="small"
            placeholder="Search options..."
            value={searchQuery}
            onChange={handleSearchChange}
            fullWidth
            variant="outlined"
            sx={componentStyles.searchInput}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={componentStyles.searchIcon} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* CONTENT */}
        <Box sx={componentStyles.content}>
          {filteredOptions.map((option) => (
            <Box
              key={option.id}
              onClick={() => handleStatusToggle(option.id)}
              sx={componentStyles.optionContainer}
            >
              <Checkbox
                size="small"
                checked={Boolean(selectedStatuses[option.id])}
                sx={componentStyles.optionCheckbox}
              />
              <Typography sx={componentStyles.optionText}>
                {option.label}
              </Typography>
            </Box>
          ))}

          {filteredOptions.length === 0 && searchQuery && (
            <Box sx={componentStyles.noResultsContainer}>
              <Typography sx={componentStyles.noResultsText}>
                No items found
              </Typography>
            </Box>
          )}
        </Box>

        {/* FOOTER */}
        <Box sx={componentStyles.footer}>
          <Box sx={componentStyles.buttonContainer}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleResetFilter}
              sx={componentStyles.resetButton}
            >
              Reset
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleApplyFilter}
              sx={componentStyles.applyButton}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default StatusFilterPopover;
